import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const RevealRequestModal = ({ candidate, onClose, onSubmit }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [feeConsent, setFeeConsent] = useState(false);
  const [antiCircumventionConsent, setAntiCircumventionConsent] = useState(false);
  const [platformIntegrityConsent, setPlatformIntegrityConsent] = useState(false);

  const handleSubmit = async () => {
    if (message?.length < 50 || !feeConsent || !antiCircumventionConsent || !platformIntegrityConsent) return;
    
    setLoading(true);
    await onSubmit(candidate?.id, message);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-semibold text-[#111827]">
            Request Identity Reveal
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div className="p-4 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="#F59E0B" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#92400E] mb-1">
                  Groupe JM Controlled Process
                </p>
                <p className="text-sm text-[#78350F]">
                  All reveal requests are now processed exclusively through Groupe JM. This request will be reviewed by Groupe JM admins and the candidate. Both parties must approve before identity is revealed. This will use 1 reveal credit from your subscription.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="#DC2626" className="flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900 mb-2">
                  Mandatory 20% Placement Fee & Anti-Circumvention Policy
                </p>
                <div className="text-sm text-red-800 space-y-2">
                  <p>
                    <strong>By submitting this reveal request, you legally agree that:</strong>
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="font-bold mt-0.5">•</span>
                      <span><strong>20% Hiring Fee:</strong> Any hire of this candidate (whether immediate or within 24 months of reveal) will trigger a mandatory placement fee of 20% of the candidate's first-year base salary, payable within 30 days of start date.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold mt-0.5">•</span>
                      <span><strong>No Circumvention:</strong> You will NOT hire this candidate outside of the BlinD platform or through any other channel. Attempting to bypass the platform to avoid fees is strictly prohibited.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold mt-0.5">•</span>
                      <span><strong>24-Month Exclusivity:</strong> This agreement remains in effect for 24 months from the reveal date. Any employment relationship established during this period must be processed through Groupe JM.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold mt-0.5">•</span>
                      <span><strong>Legal Consequences:</strong> Violation of this agreement will result in immediate account termination, legal action to recover fees, and potential additional penalties.</span>
                    </li>
                  </ul>
                  <p className="font-semibold mt-3">
                    All hiring arrangements following this reveal MUST be coordinated through Groupe JM to ensure compliance and proper fee collection.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F6F7FB] rounded-xl p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-[#111827] mb-4">
              Candidate Summary
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Title</span>
                <span className="text-sm font-medium text-[#111827]">{candidate?.title}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Experience</span>
                <span className="text-sm font-medium text-[#111827]">{candidate?.experience}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Location</span>
                <span className="text-sm font-medium text-[#111827]">{candidate?.location}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">Match Score</span>
                <span className="text-sm font-medium text-[#10B981]">{candidate?.matchScore}%</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111827] mb-2">
              Why do you want to connect with this candidate? *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder="Explain why this candidate is a good fit for your opportunity and what makes your company attractive..."
              className="w-full min-h-[120px] px-4 py-3 border border-[#E5E7EB] rounded-xl text-sm text-[#111827] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#5B2DFF] focus:border-transparent resize-none"
              required
            />
            <p className="text-xs text-[#6B7280] mt-2">
              Minimum 50 characters. Be specific and professional.
            </p>
          </div>

          <div className="space-y-3 border-t border-[#E5E7EB] pt-4">
            <h4 className="text-sm md:text-base font-semibold text-[#111827]">Required Legal Acknowledgments</h4>
            <p className="text-xs text-[#6B7280] mb-3">
              You must agree to all terms below to proceed with the reveal request:
            </p>
            
            <Checkbox
              label="I agree to pay the 20% placement fee"
              description="I understand and agree that hiring this candidate will trigger a mandatory 20% placement fee based on their first-year base salary, payable within 30 days of their start date"
              checked={feeConsent}
              onChange={(e) => setFeeConsent(e?.target?.checked)}
              required
            />

            <Checkbox
              label="I agree not to circumvent the platform"
              description="I will NOT hire this candidate outside of BlinD or through any other channel within 24 months of this reveal. All hiring arrangements must be processed through Groupe JM"
              checked={antiCircumventionConsent}
              onChange={(e) => setAntiCircumventionConsent(e?.target?.checked)}
              required
            />

            <Checkbox
              label="I understand the legal consequences of violation"
              description="I acknowledge that violating these terms will result in account termination, legal action to recover fees, and potential additional penalties"
              checked={platformIntegrityConsent}
              onChange={(e) => setPlatformIntegrityConsent(e?.target?.checked)}
              required
            />
          </div>

          <div className="flex flex-col-reverse md:flex-row gap-3">
            <Button
              variant="outline"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              fullWidth
              loading={loading}
              iconName="Send"
              iconPosition="left"
              onClick={handleSubmit}
              disabled={message?.length < 50 || !feeConsent || !antiCircumventionConsent || !platformIntegrityConsent}
            >
              Submit Request
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevealRequestModal;